import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import gsap from 'gsap';
import './WhatIsRenewed.css';

export default function WhatIsRenewed() {
  const [dismissed, setDismissed] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const sectionRef = useRef(null);
  const overlayRef = useRef(null);
  const panelRef = useRef(null);
  const revealRef = useRef(null);
  const innerRef = useRef(null);
  const tlRef = useRef(null);

  /* ── intersection observer for banner ── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.1 }
    );
    
    // Observer targets: the section itself + its descendants
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
      const els = sectionRef.current.querySelectorAll('.animate-on-scroll');
      els.forEach((el) => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  /* ── GSAP open animation ── */
  useEffect(() => {
    if (!showSidebar) return;

    document.body.style.overflow = 'hidden';

    const overlay = overlayRef.current;
    const panel = panelRef.current;
    const reveal = revealRef.current;
    const items = innerRef.current?.querySelectorAll('.rs-anim');
    if (!overlay || !panel || !reveal) return;

    if (tlRef.current) tlRef.current.kill();

    gsap.set(overlay, { opacity: 0 });
    gsap.set(panel, { x: '100%' });
    gsap.set(reveal, { scaleX: 1, transformOrigin: 'right' });
    if (items?.length) gsap.set(items, { opacity: 0, y: 30 });

    const tl = gsap.timeline();
    tlRef.current = tl;

    tl.to(overlay, { opacity: 1, duration: 0.35, ease: 'power2.out' })
      .to(panel, { x: '0%', duration: 0.55, ease: 'power4.out' }, '-=0.15')
      .to(reveal, { scaleX: 0, duration: 0.55, ease: 'power3.inOut' }, '-=0.35')
      .to(
        items,
        { opacity: 1, y: 0, stagger: 0.055, duration: 0.45, ease: 'power2.out' },
        '-=0.25'
      );
  }, [showSidebar]);

  /* ── cleanup ── */
  useEffect(() => {
    return () => {
      if (tlRef.current) tlRef.current.kill();
      document.body.style.overflow = '';
    };
  }, []);

  /* ── open ── */
  const openSidebar = () => {
    if (!showSidebar && !isAnimating) setShowSidebar(true);
  };

  /* ── close with GSAP ── */
  const closeSidebar = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);

    if (tlRef.current) tlRef.current.kill();

    const overlay = overlayRef.current;
    const panel = panelRef.current;
    const reveal = revealRef.current;
    const items = innerRef.current?.querySelectorAll('.rs-anim');

    /* make sure elements are in open state before closing */
    if (overlay) gsap.set(overlay, { opacity: 1 });
    if (panel) gsap.set(panel, { x: '0%' });
    if (reveal) gsap.set(reveal, { scaleX: 0 });
    if (items?.length) gsap.set(items, { opacity: 1, y: 0 });

    const tl = gsap.timeline({
      onComplete: () => {
        setShowSidebar(false);
        setIsAnimating(false);
        document.body.style.overflow = '';
        tlRef.current = null;
      },
    });
    tlRef.current = tl;

    if (items?.length) {
      tl.to(items, {
        opacity: 0,
        y: -14,
        stagger: 0.02,
        duration: 0.18,
        ease: 'power2.in',
      });
    }

    tl.set(reveal, { transformOrigin: 'left' })
      .to(reveal, { scaleX: 1, duration: 0.35, ease: 'power3.in' }, '-=0.05')
      .to(panel, { x: '100%', duration: 0.4, ease: 'power3.in' }, '-=0.1')
      .to(overlay, { opacity: 0, duration: 0.25, ease: 'power2.in' }, '-=0.2');
  }, [isAnimating]);

  /* ── escape key ── */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && showSidebar) closeSidebar();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showSidebar, closeSidebar]);

  if (dismissed) return null;

  /* ════════════════ RENDER ════════════════ */
  return (
    <section ref={sectionRef} className="what-is-renewed-section animate-on-scroll anim-in">
      <div className="what-is-renewed-container">
        <div className="what-is-renewed-content">
          <div className="what-is-renewed-message animate-on-scroll anim-in delay-100" onClick={openSidebar} style={{ cursor: 'pointer' }}>
            <span className="what-is-renewed-emoji wiggle">♻️</span>
            <p className="what-is-renewed-text">What is renewed or refurbished?</p>
          </div>
          <div className="what-is-renewed-actions animate-on-scroll anim-in delay-200">
            <button onClick={openSidebar} className="what-is-renewed-btn-primary">
              <span>Learn More</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
            <button onClick={() => setDismissed(true)} aria-label="Dismiss" className="what-is-renewed-close-btn">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* ─── SIDEBAR PORTAL ─── */}
      {showSidebar &&
        createPortal(
          <div className="rs-root">
            {/* overlay */}
            <div ref={overlayRef} className="rs-overlay" onClick={closeSidebar} />

            {/* panel */}
            <aside ref={panelRef} className="rs-panel" role="dialog" aria-modal="true" aria-label="Renewed devices information">
              {/* blue reveal curtain */}
              <div ref={revealRef} className="rs-reveal" />

              {/* content wrapper */}
              <div ref={innerRef} className="rs-inner">
                {/* ── HEADER ── */}
                <header className="rs-header rs-anim">
                  <div className="rs-header-left">
                    <div className="rs-header-badge">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21.5 2v6h-6" />
                        <path d="M2.5 22v-6h6" />
                        <path d="M2 11.5a10 10 0 0 1 18.8-4.3" />
                        <path d="M22 12.5a10 10 0 0 1-18.8 4.2" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="rs-header-title">Nexawave Technology</h2>
                      <p className="rs-header-sub">Hyderabad's trusted source for refurbished IT</p>
                    </div>
                  </div>
                  <button className="rs-close" onClick={closeSidebar} aria-label="Close">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                  </button>
                </header>

                {/* ── SCROLLABLE BODY ── */}
                <div className="rs-body">
                  {/* definition card */}
                  <div className="rs-definition rs-anim">
                    <div className="rs-def-accent" />
                    <p>
                      At <strong>Nexawave Technology</strong>, we specialize in high-end corporate laptops and desktops sourced directly from major companies. These aren't just "used" — they are professionally restored to peak performance through our local Hyderabad facility's rigorous testing and cleaning.
                    </p>
                  </div>


                  {/* comparison */}
                  <section className="rs-section">
                    <h3 className="rs-label rs-anim">
                      <span className="rs-label-bar" />
                      Renewed vs. Used
                    </h3>
                    <div className="rs-comparison rs-anim">
                      <div className="rs-comp-row rs-comp-head">
                        <div>Feature</div>
                        <div>Renewed</div>
                        <div>Used</div>
                      </div>
                      {[
                        { f: 'Testing', r: '72-Point', u: 'None' },
                        { f: 'Warranty', r: '6 Months', u: 'None' },
                        { f: 'Condition', r: 'Like-New', u: 'Variable' },
                        { f: 'Sanitized', r: 'Deep Clean', u: 'No' },
                      ].map((row, i) => (
                        <div className="rs-comp-row" key={i}>
                          <div>{row.f}</div>
                          <div>{row.r}</div>
                          <div>{row.u}</div>
                        </div>
                      ))}
                    </div>
                  </section>

                  {/* process */}
                  <section className="rs-section">
                    <h3 className="rs-label rs-anim">
                      <span className="rs-label-bar" />
                      Our Process
                    </h3>
                    <div className="rs-timeline">
                      {[
                        { num: '01', title: 'Source', desc: 'Direct corporate buy-backs from MNCs' },
                        { num: '02', title: 'Inspect', desc: '72-point hardware & internal check' },
                        { num: '03', title: 'Restore', desc: 'Professional cleaning & thermal repasting' },
                        { num: '04', title: 'Verify', desc: 'End-to-end performance benchmarking' },
                      ].map((step, i) => (
                        <div className="rs-step rs-anim" key={step.num}>
                          <div className="rs-step-rail">
                            <span className="rs-step-dot">{step.num}</span>
                            {i < 3 && <span className="rs-step-line" />}
                          </div>
                          <div className="rs-step-body">
                            <h4>{step.title}</h4>
                            <p>{step.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  {/* benefits */}
                  <section className="rs-section">
                    <h3 className="rs-label rs-anim">
                      <span className="rs-label-bar" />
                      Why Choose Renewed
                    </h3>
                    <div className="rs-benefits">
                      <div className="rs-benefit rs-anim">
                        <div className="rs-benefit-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0063B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 5 5 19" />
                            <circle cx="6.5" cy="6.5" r="2.5" />
                            <circle cx="17.5" cy="17.5" r="2.5" />
                          </svg>
                        </div>
                        <div>
                          <h4>Save 30–70%</h4>
                          <p>Premium devices at a fraction of retail price</p>
                        </div>
                      </div>


                      <div className="rs-benefit rs-anim">
                        <div className="rs-benefit-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0063B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                          </svg>
                        </div>
                        <div>
                          <h4>Like-New Quality</h4>
                          <p>Rigorous testing ensures full functionality</p>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* what's in the box */}
                  <section className="rs-section">
                    <h3 className="rs-label rs-anim">
                      <span className="rs-label-bar" />
                      What's in the Box?
                    </h3>
                    <div className="rs-box-content rs-anim">
                      <p className="rs-box-intro">Your Nexawave device comes ready for work with everything you need:</p>
                      <ul className="rs-box-list">
                        <li><span>✓</span> Professionally Refurbished PC / Laptop</li>
                        <li><span>✓</span> Certified Power Adapter & Cable</li>
                        <li><span>✓</span> Clean OS Pre-installed & Activated</li>
                        <li><span>✓</span> 6-Month Official Nexawave Warranty</li>
                      </ul>
                    </div>
                  </section>

                  {/* guarantee */}
                  <div className="rs-guarantee rs-anim">
                    <div className="rs-guarantee-top">
                      <div className="rs-guarantee-badge">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0063B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                          <path d="m9 12 2 2 4-4" />
                        </svg>
                      </div>
                      <h4>Quality Guarantee</h4>
                    </div>
                    <div className="rs-tags">
                      <span className="rs-tag">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                        7-Day Replacement
                      </span>
                      <span className="rs-tag">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                        6-Month Warranty
                      </span>
                      <span className="rs-tag">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                        Free Support
                      </span>
                    </div>
                  </div>
                </div>

                {/* ── FOOTER ── */}
                <footer className="rs-footer rs-anim">
                  <button className="rs-cta" onClick={closeSidebar}>
                    <span>Shop Renewed Devices</span>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </button>
                </footer>
              </div>
            </aside>
          </div>,
          document.body
        )}
    </section>
  );
}